/**
 * Palette — Left sidebar with draggable BPMN elements organized by category.
 *
 * Features:
 * - Expandable/collapsible groups (collapsed by default)
 * - Search/filter input
 * - Compact (icon-only) / expanded (icon + label) toggle
 * - Color-coded category headers
 * - Modern UI with smooth animations
 * - Theme-aware icons and group colors
 *
 * Uses LogicFlow's native dnd.startDrag() for drag-to-canvas.
 */

import { DEFAULT_THEME } from './theme';

// ── SVG Icons (generated from theme) ────────────────────────────────────────

function buildPaletteIcons(theme) {
  const t = theme || DEFAULT_THEME;
  return {
  // Events
  startEvent: `<svg width="20" height="20"><circle cx="10" cy="10" r="9" fill="${t.startEvent.fill}" stroke="${t.startEvent.stroke}" stroke-width="2"/></svg>`,
  endEvent: `<svg width="20" height="20"><circle cx="10" cy="10" r="9" fill="${t.endEvent.fill}" stroke="${t.endEvent.stroke}" stroke-width="3"/></svg>`,
  intermediateEvent: `<svg width="20" height="20">
    <circle cx="10" cy="10" r="9" fill="${t.intermediateEvent.fill}" stroke="${t.intermediateEvent.stroke}" stroke-width="2"/>
    <circle cx="10" cy="10" r="6" fill="none" stroke="${t.intermediateEvent.stroke}" stroke-width="1.5"/>
  </svg>`,
  intermediateThrowEvent: `<svg width="20" height="20">
    <circle cx="10" cy="10" r="9" fill="${t.intermediateThrowEvent.fill}" stroke="${t.intermediateThrowEvent.stroke}" stroke-width="2"/>
    <circle cx="10" cy="10" r="6" fill="none" stroke="${t.intermediateThrowEvent.stroke}" stroke-width="1.5"/>
    <polygon points="10,5 13.5,11.5 6.5,11.5" fill="${t.intermediateThrowEvent.stroke}" stroke="${t.intermediateThrowEvent.stroke}" stroke-width="0.5"/>
  </svg>`,
  boundaryEvent: `<svg width="20" height="20">
    <circle cx="10" cy="10" r="8" fill="${t.boundaryEvent.fill}" stroke="${t.boundaryEvent.stroke}" stroke-width="2"/>
    <circle cx="10" cy="10" r="5.5" fill="none" stroke="${t.boundaryEvent.stroke}" stroke-width="1"/>
  </svg>`,

  // Tasks
  userTask: `<svg width="22" height="16">
    <rect width="22" height="16" rx="2" fill="${t.userTask.fill}" stroke="${t.userTask.stroke}" stroke-width="1"/>
    <circle cx="7" cy="6" r="2.2" fill="none" stroke="${t.userTask.stroke}" stroke-width="0.9"/>
    <path d="M 3.5 13 Q 7 9.5 10.5 13" fill="none" stroke="${t.userTask.stroke}" stroke-width="0.9"/>
  </svg>`,
  serviceTask: `<svg width="22" height="16">
    <rect width="22" height="16" rx="2" fill="${t.serviceTask.fill}" stroke="${t.serviceTask.stroke}" stroke-width="1"/>
    <circle cx="8" cy="8" r="3.5" fill="none" stroke="${t.serviceTask.stroke}" stroke-width="0.9"/>
    <circle cx="8" cy="8" r="1.2" fill="${t.serviceTask.stroke}"/>
  </svg>`,
  scriptTask: `<svg width="22" height="16">
    <rect width="22" height="16" rx="2" fill="${t.scriptTask.fill}" stroke="${t.scriptTask.stroke}" stroke-width="1"/>
    <line x1="5" y1="5" x2="13" y2="5" stroke="${t.scriptTask.stroke}" stroke-width="0.9"/>
    <line x1="5" y1="8" x2="13" y2="8" stroke="${t.scriptTask.stroke}" stroke-width="0.9"/>
    <line x1="5" y1="11" x2="10" y2="11" stroke="${t.scriptTask.stroke}" stroke-width="0.9"/>
  </svg>`,
  manualTask: `<svg width="22" height="16">
    <rect width="22" height="16" rx="2" fill="${t.manualTask.fill}" stroke="${t.manualTask.stroke}" stroke-width="1"/>
    <path d="M 6 12 L 6 7 C 6 5 8 4 9 6 L 10 5 C 11 4 12 5 12 6 L 13 6 L 13 12 Z" fill="none" stroke="${t.manualTask.stroke}" stroke-width="0.9"/>
  </svg>`,
  sendTask: `<svg width="22" height="16">
    <rect width="22" height="16" rx="2" fill="${t.sendTask.fill}" stroke="${t.sendTask.stroke}" stroke-width="1"/>
    <path d="M 5 4 L 11 8 L 17 4 L 17 12 L 5 12 Z" fill="${t.sendTask.stroke}" stroke="${t.sendTask.stroke}" stroke-width="0.8"/>
    <path d="M 5 4 L 11 8 L 17 4" fill="none" stroke="#fff" stroke-width="0.8"/>
  </svg>`,
  receiveTask: `<svg width="22" height="16">
    <rect width="22" height="16" rx="2" fill="${t.receiveTask.fill}" stroke="${t.receiveTask.stroke}" stroke-width="1"/>
    <rect x="5" y="4" width="12" height="8" fill="none" stroke="${t.receiveTask.stroke}" stroke-width="0.9"/>
    <path d="M 5 4 L 11 8 L 17 4" fill="none" stroke="${t.receiveTask.stroke}" stroke-width="0.9"/>
  </svg>`,
  businessRuleTask: `<svg width="22" height="16">
    <rect width="22" height="16" rx="2" fill="${t.businessRuleTask.fill}" stroke="${t.businessRuleTask.stroke}" stroke-width="1"/>
    <rect x="5" y="3" width="12" height="10" fill="none" stroke="${t.businessRuleTask.stroke}" stroke-width="0.8"/>
    <rect x="5" y="3" width="12" height="3.5" fill="${t.businessRuleTask.stroke}" stroke="${t.businessRuleTask.stroke}" stroke-width="0.8"/>
    <line x1="5" y1="9.5" x2="17" y2="9.5" stroke="${t.businessRuleTask.stroke}" stroke-width="0.6"/>
    <line x1="9" y1="6.5" x2="9" y2="13" stroke="${t.businessRuleTask.stroke}" stroke-width="0.6"/>
  </svg>`,
  callActivity: `<svg width="22" height="16">
    <rect width="22" height="16" rx="2" fill="${t.callActivity.fill}" stroke="${t.callActivity.stroke}" stroke-width="2"/>
    <path d="M 6 5 L 12 5 L 12 3 L 16 8 L 12 13 L 12 11 L 6 11 Z" fill="none" stroke="${t.callActivity.stroke}" stroke-width="0.9"/>
  </svg>`,
  aiServiceTask: `<svg width="22" height="16">
    <rect width="22" height="16" rx="2" fill="${t.serviceTask.fill}" stroke="${t.serviceTask.stroke}" stroke-width="1"/>
    <rect x="2" y="2" width="11" height="7" rx="1.4" fill="#fff0f6" stroke="#c41d7f" stroke-width="0.9"/>
    <text x="7.5" y="7.6" text-anchor="middle" font-size="5.5" font-weight="700" fill="#c41d7f" font-family="ui-monospace,Menlo,monospace">AI</text>
    <circle cx="16" cy="11" r="2.6" fill="none" stroke="${t.serviceTask.stroke}" stroke-width="0.9"/>
    <circle cx="16" cy="11" r="0.9" fill="${t.serviceTask.stroke}"/>
  </svg>`,
  aiAgent: `<svg width="22" height="18">
    <rect width="22" height="18" rx="2" fill="${t.subProcess.fill}" stroke="${t.subProcess.stroke}" stroke-width="2"/>
    <rect x="2" y="2" width="13" height="7" rx="1.4" fill="#fff0f6" stroke="#c41d7f" stroke-width="0.9"/>
    <text x="8.5" y="7.6" text-anchor="middle" font-size="5" font-weight="700" fill="#c41d7f" font-family="ui-monospace,Menlo,monospace">AI·AGT</text>
    <rect x="7" y="11" width="8" height="6" rx="1" fill="none" stroke="${t.subProcess.stroke}" stroke-width="0.8"/>
    <path d="M 11 12.5 L 11 15.5 M 9.5 14 L 12.5 14" stroke="${t.subProcess.stroke}" stroke-width="0.7"/>
  </svg>`,

  // Gateways
  exclusiveGateway: `<svg width="20" height="20">
    <polygon points="10,1 19,10 10,19 1,10" fill="${t.exclusiveGateway.fill}" stroke="${t.exclusiveGateway.stroke}" stroke-width="2"/>
    <path d="M 7 7 L 13 13 M 13 7 L 7 13" stroke="${t.exclusiveGateway.stroke}" stroke-width="1.5" fill="none"/>
  </svg>`,
  parallelGateway: `<svg width="20" height="20">
    <polygon points="10,1 19,10 10,19 1,10" fill="${t.parallelGateway.fill}" stroke="${t.parallelGateway.stroke}" stroke-width="2"/>
    <path d="M 10 5 L 10 15 M 5 10 L 15 10" stroke="${t.parallelGateway.stroke}" stroke-width="2" fill="none"/>
  </svg>`,
  inclusiveGateway: `<svg width="20" height="20">
    <polygon points="10,1 19,10 10,19 1,10" fill="${t.inclusiveGateway.fill}" stroke="${t.inclusiveGateway.stroke}" stroke-width="2"/>
    <circle cx="10" cy="10" r="4" fill="none" stroke="${t.inclusiveGateway.stroke}" stroke-width="1.5"/>
  </svg>`,
  complexGateway: `<svg width="20" height="20">
    <polygon points="10,1 19,10 10,19 1,10" fill="${t.complexGateway.fill}" stroke="${t.complexGateway.stroke}" stroke-width="2"/>
    <path d="M 10 5 L 10 15 M 5 10 L 15 10 M 7 7 L 13 13 M 13 7 L 7 13" stroke="${t.complexGateway.stroke}" stroke-width="1.2" fill="none"/>
  </svg>`,
  eventBasedGateway: `<svg width="20" height="20">
    <polygon points="10,1 19,10 10,19 1,10" fill="${t.eventBasedGateway.fill}" stroke="${t.eventBasedGateway.stroke}" stroke-width="2"/>
    <circle cx="10" cy="10" r="5" fill="none" stroke="${t.eventBasedGateway.stroke}" stroke-width="1"/>
    <circle cx="10" cy="10" r="3.5" fill="none" stroke="${t.eventBasedGateway.stroke}" stroke-width="1"/>
  </svg>`,

  // Sub-Process
  subProcess: `<svg width="22" height="18">
    <rect width="22" height="18" rx="2" fill="${t.subProcess.fill}" stroke="${t.subProcess.stroke}" stroke-width="2"/>
    <rect x="7" y="10" width="8" height="6" rx="1" fill="none" stroke="${t.subProcess.stroke}" stroke-width="0.8"/>
    <path d="M 11 11.5 L 11 14.5 M 9.5 13 L 12.5 13" stroke="${t.subProcess.stroke}" stroke-width="0.7"/>
  </svg>`,
  transaction: `<svg width="22" height="18">
    <rect width="22" height="18" rx="2" fill="${t.transaction.fill}" stroke="${t.transaction.stroke}" stroke-width="2"/>
    <rect x="2" y="2" width="18" height="14" rx="1" fill="none" stroke="${t.transaction.stroke}" stroke-width="1"/>
    <rect x="7" y="10" width="8" height="6" rx="1" fill="none" stroke="${t.transaction.stroke}" stroke-width="0.8"/>
    <path d="M 11 11.5 L 11 14.5 M 9.5 13 L 12.5 13" stroke="${t.transaction.stroke}" stroke-width="0.7"/>
  </svg>`,
  eventSubProcess: `<svg width="22" height="18">
    <rect width="22" height="18" rx="2" fill="${t.subProcess.fill}" stroke="${t.subProcess.stroke}" stroke-width="2" stroke-dasharray="3,2"/>
    <rect x="7" y="10" width="8" height="6" rx="1" fill="none" stroke="${t.subProcess.stroke}" stroke-width="0.8"/>
    <path d="M 11 11.5 L 11 14.5 M 9.5 13 L 12.5 13" stroke="${t.subProcess.stroke}" stroke-width="0.7"/>
  </svg>`,

  // Participants (Pools & Lanes)
  participant: `<svg width="22" height="18">
    <rect x="0" y="0" width="22" height="18" fill="${t.participant.fill}" stroke="${t.participant.stroke}" stroke-width="1.5"/>
    <line x1="5" y1="0" x2="5" y2="18" stroke="${t.participant.stroke}" stroke-width="1"/>
    <text x="2.5" y="12" font-size="5" fill="${(t.text && t.text.fill) || '#333'}" transform="rotate(-90, 2.5, 10)" text-anchor="middle">P</text>
  </svg>`,
  lane: `<svg width="22" height="18">
    <rect x="0" y="0" width="22" height="18" fill="${t.lane.fill}" stroke="${t.lane.stroke}" stroke-width="1"/>
    <rect x="0" y="0" width="5" height="18" fill="${t.lane.fill}" stroke="${t.lane.stroke}" stroke-width="0.8"/>
    <text x="2.5" y="12" font-size="5" fill="${(t.text && t.text.fill) || '#333'}" transform="rotate(-90, 2.5, 10)" text-anchor="middle">L</text>
  </svg>`,

  // Data & Artifacts
  dataObject: `<svg width="18" height="22">
    <path d="M 1 0 L 12 0 L 17 5 L 17 22 L 1 22 Z" fill="${t.dataObjectReference.fill}" stroke="${t.dataObjectReference.stroke}" stroke-width="1.2"/>
    <path d="M 12 0 L 12 5 L 17 5" fill="none" stroke="${t.dataObjectReference.stroke}" stroke-width="1"/>
  </svg>`,
  dataStore: `<svg width="20" height="20">
    <ellipse cx="10" cy="4" rx="9" ry="3.5" fill="${t.dataStoreReference.fill}" stroke="${t.dataStoreReference.stroke}" stroke-width="1.2"/>
    <path d="M 1 4 L 1 16" fill="none" stroke="${t.dataStoreReference.stroke}" stroke-width="1.2"/>
    <path d="M 19 4 L 19 16" fill="none" stroke="${t.dataStoreReference.stroke}" stroke-width="1.2"/>
    <ellipse cx="10" cy="16" rx="9" ry="3.5" fill="${t.dataStoreReference.fill}" stroke="${t.dataStoreReference.stroke}" stroke-width="1.2"/>
  </svg>`,
  group: `<svg width="22" height="18">
    <rect x="1" y="1" width="20" height="16" rx="4" fill="none" stroke="${t.group.stroke}" stroke-width="1.2" stroke-dasharray="5,3"/>
  </svg>`,
  textAnnotation: `<svg width="22" height="18">
    <path d="M 6 1 L 1 1 L 1 17 L 6 17" fill="none" stroke="${t.textAnnotation.stroke}" stroke-width="1.5"/>
    <line x1="4" y1="6" x2="18" y2="6" stroke="${t.textAnnotation.stroke}" stroke-width="0.8"/>
    <line x1="4" y1="9.5" x2="16" y2="9.5" stroke="${t.textAnnotation.stroke}" stroke-width="0.8"/>
    <line x1="4" y1="13" x2="12" y2="13" stroke="${t.textAnnotation.stroke}" stroke-width="0.8"/>
  </svg>`,

  };
}

// Static tool/UI icons (not theme-dependent)
const STATIC_ICONS = {
  lasso: `<svg width="16" height="16" viewBox="0 0 16 16">
    <rect x="2" y="2" width="12" height="12" fill="none" stroke="#555" stroke-width="1.3" stroke-dasharray="3,2" rx="1"/>
  </svg>`,
  connect: `<svg width="16" height="16" viewBox="0 0 16 16">
    <path d="M 2 14 L 14 2" stroke="#555" stroke-width="1.5"/>
    <polygon points="14,2 10,3 13,6" fill="#555"/>
  </svg>`,
  zoomIn: `<svg width="16" height="16" viewBox="0 0 16 16">
    <circle cx="7" cy="7" r="5.5" fill="none" stroke="#555" stroke-width="1.3"/>
    <line x1="7" y1="4.5" x2="7" y2="9.5" stroke="#555" stroke-width="1.3" stroke-linecap="round"/>
    <line x1="4.5" y1="7" x2="9.5" y2="7" stroke="#555" stroke-width="1.3" stroke-linecap="round"/>
    <line x1="11" y1="11" x2="14.5" y2="14.5" stroke="#555" stroke-width="1.5" stroke-linecap="round"/>
  </svg>`,
  zoomOut: `<svg width="16" height="16" viewBox="0 0 16 16">
    <circle cx="7" cy="7" r="5.5" fill="none" stroke="#555" stroke-width="1.3"/>
    <line x1="4.5" y1="7" x2="9.5" y2="7" stroke="#555" stroke-width="1.3" stroke-linecap="round"/>
    <line x1="11" y1="11" x2="14.5" y2="14.5" stroke="#555" stroke-width="1.5" stroke-linecap="round"/>
  </svg>`,
  fitView: `<svg width="16" height="16" viewBox="0 0 16 16">
    <rect x="3" y="3" width="10" height="10" rx="1.5" fill="none" stroke="#555" stroke-width="1.2"/>
    <path d="M 1 5 L 1 1 L 5 1" fill="none" stroke="#555" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/>
    <path d="M 11 1 L 15 1 L 15 5" fill="none" stroke="#555" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/>
    <path d="M 15 11 L 15 15 L 11 15" fill="none" stroke="#555" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/>
    <path d="M 5 15 L 1 15 L 1 11" fill="none" stroke="#555" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/>
  </svg>`,
  autoLayout: `<svg width="16" height="16" viewBox="0 0 16 16">
    <circle cx="2.5" cy="8" r="1.8" fill="none" stroke="#555" stroke-width="1.2"/>
    <rect x="5.5" y="3" width="4" height="3" rx="1" fill="none" stroke="#555" stroke-width="1.2"/>
    <rect x="5.5" y="10" width="4" height="3" rx="1" fill="none" stroke="#555" stroke-width="1.2"/>
    <rect x="11" y="5.5" width="4" height="5" rx="1" fill="none" stroke="#555" stroke-width="1.2"/>
    <line x1="4.3" y1="7.2" x2="5.5" y2="5.5" stroke="#555" stroke-width="1" stroke-linecap="round"/>
    <line x1="4.3" y1="8.8" x2="5.5" y2="11" stroke="#555" stroke-width="1" stroke-linecap="round"/>
    <line x1="9.5" y1="4.5" x2="11" y2="7" stroke="#555" stroke-width="1" stroke-linecap="round"/>
    <line x1="9.5" y1="11.5" x2="11" y2="9" stroke="#555" stroke-width="1" stroke-linecap="round"/>
  </svg>`,

  // UI icons
  search: `<svg width="14" height="14" viewBox="0 0 16 16">
    <circle cx="7" cy="7" r="5" fill="none" stroke="#999" stroke-width="1.5"/>
    <line x1="11" y1="11" x2="14" y2="14" stroke="#999" stroke-width="1.5" stroke-linecap="round"/>
  </svg>`,
  expand: `<svg width="14" height="14" viewBox="0 0 16 16">
    <path d="M 2 5 L 8 5 M 2 8 L 8 8 M 2 11 L 8 11 M 10 5 L 14 5 M 10 8 L 14 8 M 10 11 L 14 11" stroke="#666" stroke-width="1.5" stroke-linecap="round"/>
  </svg>`,
  collapse: `<svg width="14" height="14" viewBox="0 0 16 16">
    <rect x="3" y="3" width="4" height="4" rx="1" fill="#666"/>
    <rect x="9" y="3" width="4" height="4" rx="1" fill="#666"/>
    <rect x="3" y="9" width="4" height="4" rx="1" fill="#666"/>
    <rect x="9" y="9" width="4" height="4" rx="1" fill="#666"/>
  </svg>`,
};

function buildGroupThemes(theme) {
  const t = theme || DEFAULT_THEME;
  return {
    events:        { bg: t.startEvent.fill, border: t.startEvent.stroke + '66', accent: t.startEvent.stroke, icon: 'startEvent' },
    tasks:         { bg: t.userTask.fill, border: t.userTask.stroke + '66', accent: t.userTask.stroke, icon: 'userTask' },
    gateways:      { bg: t.exclusiveGateway.fill, border: t.exclusiveGateway.stroke + '66', accent: t.exclusiveGateway.stroke, icon: 'exclusiveGateway' },
    subProcesses:  { bg: t.subProcess.fill, border: t.subProcess.stroke + '66', accent: t.subProcess.stroke, icon: 'subProcess' },
    ai:            { bg: '#fff0f6', border: '#c41d7f66', accent: '#c41d7f', icon: 'aiServiceTask' },
    participants:  { bg: t.participant.fill, border: t.participant.stroke + '44', accent: t.participant.stroke, icon: 'participant' },
    dataArtifacts: { bg: t.dataObjectReference.fill, border: t.dataObjectReference.stroke + '44', accent: t.dataObjectReference.stroke, icon: 'dataObject' },
  };
}

// ── Palette Group Definitions ────────────────────────────────────────────────

// Feature flag — gates the experimental "ai" group (AI Service Task, AI Agent).
// Webpack DefinePlugin in sfd-v2 inlines ``process.env.AI_NODES_ENABLED``; in
// non-bundled contexts (tests, standalone usage) it falls back to OFF.
const _AI_NODES_ENABLED = (() => {
  try {
    return typeof process !== 'undefined'
      && process.env
      && process.env.AI_NODES_ENABLED === 'true';
  } catch (_) {
    return false;
  }
})();

const _ALL_PALETTE_GROUPS = [
  {
    id: 'events',
    i18nKey: 'events',
    items: [
      { type: 'epx:StartEvent', i18nKey: 'startEvent', icon: 'startEvent' },
      { type: 'epx:EndEvent', i18nKey: 'endEvent', icon: 'endEvent' },
      { type: 'epx:IntermediateEvent', i18nKey: 'intermediateEvent', icon: 'intermediateEvent' },
      { type: 'epx:IntermediateThrowEvent', i18nKey: 'intermediateThrowEvent', icon: 'intermediateThrowEvent' },
      { type: 'epx:BoundaryEvent', i18nKey: 'boundaryEvent', icon: 'boundaryEvent' },
    ],
  },
  {
    id: 'tasks',
    i18nKey: 'tasks',
    items: [
      { type: 'epx:UserTask', i18nKey: 'userTask', icon: 'userTask' },
      { type: 'epx:ServiceTask', i18nKey: 'serviceTask', icon: 'serviceTask' },
      { type: 'epx:ScriptTask', i18nKey: 'scriptTask', icon: 'scriptTask' },
      { type: 'epx:ManualTask', i18nKey: 'manualTask', icon: 'manualTask' },
      { type: 'epx:SendTask', i18nKey: 'sendTask', icon: 'sendTask' },
      { type: 'epx:ReceiveTask', i18nKey: 'receiveTask', icon: 'receiveTask' },
      { type: 'epx:BusinessRuleTask', i18nKey: 'businessRuleTask', icon: 'businessRuleTask' },
      { type: 'epx:CallActivity', i18nKey: 'callActivity', icon: 'callActivity' },
    ],
  },
  {
    id: 'gateways',
    i18nKey: 'gateways',
    items: [
      { type: 'epx:ExclusiveGateway', i18nKey: 'exclusiveGateway', icon: 'exclusiveGateway' },
      { type: 'epx:ParallelGateway', i18nKey: 'parallelGateway', icon: 'parallelGateway' },
      { type: 'epx:InclusiveGateway', i18nKey: 'inclusiveGateway', icon: 'inclusiveGateway' },
      { type: 'epx:ComplexGateway', i18nKey: 'complexGateway', icon: 'complexGateway' },
      { type: 'epx:EventBasedGateway', i18nKey: 'eventBasedGateway', icon: 'eventBasedGateway' },
    ],
  },
  {
    id: 'subProcesses',
    i18nKey: 'subProcesses',
    items: [
      { type: 'epx:SubProcess', i18nKey: 'subProcess', icon: 'subProcess' },
      { type: 'epx:Transaction', i18nKey: 'transaction', icon: 'transaction' },
      { type: 'epx:SubProcess', i18nKey: 'eventSubProcess', icon: 'eventSubProcess', properties: { triggeredByEvent: true } },
    ],
  },
  {
    id: 'ai',
    i18nKey: 'ai',
    items: [
      { type: 'epx:ServiceTask', i18nKey: 'aiServiceTask', icon: 'aiServiceTask', properties: { aiKind: 'llm-prompt' } },
      { type: 'epx:SubProcess',  i18nKey: 'aiAgent',       icon: 'aiAgent',       properties: { isAiAgent: true } },
    ],
  },
  {
    id: 'participants',
    i18nKey: 'participants',
    items: [
      { type: 'epx:Participant', i18nKey: 'participant', icon: 'participant' },
    ],
  },
  {
    id: 'dataArtifacts',
    i18nKey: 'dataArtifacts',
    items: [
      { type: 'epx:DataObjectReference', i18nKey: 'dataObject', icon: 'dataObject' },
      { type: 'epx:DataStoreReference', i18nKey: 'dataStore', icon: 'dataStore' },
      { type: 'epx:Group', i18nKey: 'group', icon: 'group' },
      { type: 'epx:TextAnnotation', i18nKey: 'textAnnotation', icon: 'textAnnotation' },
    ],
  },
];

// Active palette — drops the 'ai' group when the feature flag is OFF so the
// AI nodes don't show up in the canvas palette / search.
const PALETTE_GROUPS = _AI_NODES_ENABLED
  ? _ALL_PALETTE_GROUPS
  : _ALL_PALETTE_GROUPS.filter((g) => g.id !== 'ai');

const TOOL_ITEMS = [
  { id: 'lasso', icon: 'lasso', i18nKey: 'lasso' },
  { id: 'connect', icon: 'connect', i18nKey: 'connect' },
];

const ACTION_ITEMS = [
  { id: 'zoomIn',     icon: 'zoomIn',     i18nKey: 'zoomIn' },
  { id: 'zoomOut',    icon: 'zoomOut',    i18nKey: 'zoomOut' },
  { id: 'fitView',    icon: 'fitView',    i18nKey: 'fitView' },
  { id: 'autoLayout', icon: 'autoLayout', i18nKey: 'autoLayout' },
];

// ── CSS (injected once) ──────────────────────────────────────────────────────

let cssInjected = false;

function injectCSS() {
  if (cssInjected) return;
  cssInjected = true;
  const style = document.createElement('style');
  style.textContent = `
    /* ── Root container ── */
    .epx-palette {
      width: 200px;
      background: var(--epx-panel-bg, #fff);
      border: 1px solid var(--epx-panel-border, #e0e0e0);
      border-radius: 8px;
      box-shadow: 0 2px 12px rgba(0,0,0,0.08);
      padding: 0;
      max-height: 100%;
      overflow: hidden;
      font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
      font-size: 12px;
      user-select: none;
      display: flex;
      flex-direction: column;
      transition: width 0.25s ease;
    }
    .epx-palette.compact {
      width: 56px;
    }

    /* ── Scrollbar styling ── */
    .epx-palette::-webkit-scrollbar {
      width: 4px;
    }
    .epx-palette::-webkit-scrollbar-track {
      background: transparent;
    }
    .epx-palette::-webkit-scrollbar-thumb {
      background: var(--epx-panel-border, #d9d9d9);
      border-radius: 2px;
    }
    .epx-palette::-webkit-scrollbar-thumb:hover {
      background: var(--epx-panel-input-border, #bfbfbf);
    }

    /* ── Top bar (search + toggle) ── */
    .epx-palette-topbar {
      display: flex;
      align-items: center;
      gap: 4px;
      padding: 8px 8px 4px 8px;
      flex-shrink: 0;
    }
    .epx-palette.compact .epx-palette-topbar {
      justify-content: center;
      padding: 8px 4px 4px 4px;
    }

    .epx-palette-search-wrap {
      flex: 1;
      position: relative;
      display: flex;
      align-items: center;
    }
    .epx-palette.compact .epx-palette-search-wrap {
      display: none;
    }
    .epx-palette-search-icon {
      position: absolute;
      left: 6px;
      top: 50%;
      transform: translateY(-50%);
      pointer-events: none;
      display: flex;
      align-items: center;
    }
    .epx-palette-search {
      width: 100%;
      height: 28px;
      border: 1px solid var(--epx-panel-input-border, #e0e0e0);
      border-radius: 6px;
      padding: 0 8px 0 24px;
      font-size: 11px;
      outline: none;
      background: var(--epx-panel-input-bg, #fafafa);
      color: var(--epx-panel-text, #333);
      transition: border-color 0.2s, background 0.2s;
      font-family: inherit;
    }
    .epx-palette-search:focus {
      border-color: var(--epx-panel-accent-text, #1890ff);
      background: var(--epx-panel-bg, #fff);
      box-shadow: 0 0 0 2px rgba(24,144,255,0.1);
    }
    .epx-palette-search::placeholder {
      color: var(--epx-panel-text-muted, #bbb);
    }

    .epx-palette-toggle-btn {
      width: 28px;
      height: 28px;
      display: flex;
      align-items: center;
      justify-content: center;
      border: 1px solid var(--epx-panel-border, #e0e0e0);
      border-radius: 6px;
      background: var(--epx-panel-header-bg, #fafafa);
      cursor: pointer;
      flex-shrink: 0;
      transition: background 0.15s, border-color 0.15s;
    }
    .epx-palette-toggle-btn:hover {
      background: var(--epx-panel-accent-bg, #e6f7ff);
      border-color: var(--epx-panel-accent-border, #91d5ff);
    }

    /* ── Tools section ── */
    .epx-palette-tools {
      display: flex;
      justify-content: center;
      gap: 2px;
      padding: 6px 8px;
      flex-shrink: 0;
    }
    .epx-palette.compact .epx-palette-tools {
      flex-wrap: wrap;
      padding: 6px 4px;
    }
    .epx-palette-tool-btn {
      width: 28px;
      height: 28px;
      display: flex;
      align-items: center;
      justify-content: center;
      border: 1px solid transparent;
      border-radius: 6px;
      background: none;
      cursor: pointer;
      padding: 2px;
      transition: all 0.15s;
      position: relative;
    }
    .epx-palette-tool-btn:hover {
      background: var(--epx-panel-hover-bg, #f0f0f0);
      border-color: var(--epx-panel-border, #d9d9d9);
    }
    .epx-palette-tool-btn.active {
      background: var(--epx-panel-accent-bg, #e6f7ff);
      border-color: var(--epx-panel-accent-text, #1890ff);
      color: var(--epx-panel-accent-text, #1890ff);
    }

    /* ── Divider ── */
    .epx-palette-divider {
      height: 0;
      margin: 2px 10px;
      border-top: 1px solid var(--epx-panel-border, #f0f0f0);
      flex-shrink: 0;
    }
    .epx-palette.compact .epx-palette-divider {
      margin: 2px 6px;
    }

    /* ── Groups container (scrollable) ── */
    .epx-palette-groups {
      flex: 1;
      min-height: 0;
      overflow-y: auto;
      overflow-x: hidden;
      padding-bottom: 8px;
    }
    .epx-palette-groups::-webkit-scrollbar {
      width: 4px;
    }
    .epx-palette-groups::-webkit-scrollbar-track {
      background: transparent;
    }
    .epx-palette-groups::-webkit-scrollbar-thumb {
      background: var(--epx-panel-border, #d9d9d9);
      border-radius: 2px;
    }

    /* ── Group ── */
    .epx-palette-group {
      margin: 2px 6px;
      border-radius: 6px;
      overflow: hidden;
      transition: margin 0.2s;
    }
    .epx-palette.compact .epx-palette-group {
      margin: 2px 3px;
    }

    /* ── Group Header ── */
    .epx-palette-group-header {
      padding: 7px 10px;
      font-size: 11px;
      font-weight: 600;
      color: var(--epx-panel-text-secondary, #555);
      cursor: pointer;
      display: flex;
      align-items: center;
      gap: 8px;
      border-radius: 6px;
      transition: all 0.15s;
      border: 1px solid transparent;
    }
    .epx-palette-group-header:hover {
      opacity: 0.85;
    }
    .epx-palette-group-header.expanded {
      border-radius: 6px 6px 0 0;
    }

    .epx-palette.compact .epx-palette-group-header {
      justify-content: center;
      padding: 8px 4px;
      gap: 0;
    }

    .epx-palette-group-header-icon {
      width: 20px;
      height: 20px;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
    }
    .epx-palette-group-header-label {
      flex: 1;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      font-size: 10px;
    }
    .epx-palette.compact .epx-palette-group-header-label {
      display: none;
    }
    .epx-palette-group-chevron {
      font-size: 10px;
      transition: transform 0.2s ease;
      flex-shrink: 0;
      color: var(--epx-panel-text-muted, #999);
    }
    .epx-palette-group-chevron.collapsed {
      transform: rotate(-90deg);
    }
    .epx-palette.compact .epx-palette-group-chevron {
      display: none;
    }
    .epx-palette-group-count {
      font-size: 9px;
      font-weight: 500;
      color: var(--epx-panel-text-muted, #999);
      background: var(--epx-panel-hover-bg, #f0f0f0);
      border-radius: 8px;
      padding: 1px 5px;
      flex-shrink: 0;
    }
    .epx-palette.compact .epx-palette-group-count {
      display: none;
    }

    /* ── Group Body ── */
    .epx-palette-group-body {
      max-height: 500px;
      overflow: hidden;
      transition: max-height 0.25s ease, opacity 0.2s ease;
      opacity: 1;
      border-left: 1px solid transparent;
      border-right: 1px solid transparent;
      border-bottom: 1px solid transparent;
      border-radius: 0 0 6px 6px;
    }
    .epx-palette-group-body.collapsed {
      max-height: 0;
      opacity: 0;
    }

    /* ── Palette Item ── */
    .epx-palette-item {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 6px 10px;
      cursor: grab;
      transition: background 0.12s;
      position: relative;
    }
    .epx-palette.compact .epx-palette-item {
      justify-content: center;
      padding: 6px 4px;
      gap: 0;
    }
    .epx-palette-item:hover {
      background: var(--epx-panel-hover-bg, rgba(24,144,255,0.06));
    }
    .epx-palette-item:active {
      cursor: grabbing;
      background: var(--epx-panel-accent-bg, rgba(24,144,255,0.12));
    }
    .epx-palette-item.filtered-out {
      display: none;
    }

    .epx-palette-icon {
      width: 26px;
      height: 26px;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
    }
    .epx-palette-item-label {
      font-size: 11.5px;
      color: var(--epx-panel-text, #444);
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      flex: 1;
    }
    .epx-palette.compact .epx-palette-item-label {
      display: none;
    }

    /* ── No results ── */
    .epx-palette-no-results {
      padding: 16px 10px;
      text-align: center;
      color: var(--epx-panel-text-muted, #bbb);
      font-size: 11px;
      font-style: italic;
    }

    /* ── Lasso rect ── */
    .epx-lasso-rect {
      position: absolute;
      border: 1px dashed var(--epx-panel-accent-text, #1890ff);
      background: var(--epx-panel-accent-bg, rgba(24,144,255,0.08));
      pointer-events: none;
      z-index: 150;
    }
  `;
  document.head.appendChild(style);
}

// ── Palette class ────────────────────────────────────────────────────────────

export class Palette {
  constructor(lf, container, i18n, opts = {}) {
    this._lf = lf;
    this._i18n = i18n || {};
    this._onFitView          = opts.onFitView          || null;
    this._onAutoLayout       = opts.onAutoLayout       || null;
    this._onSelectionChanged = opts.onSelectionChanged || null;
    this._containerEl = typeof container === 'string'
      ? document.querySelector(container)
      : container;
    this._activeTool = null;
    // All groups collapsed by default
    this._collapsedGroups = new Set(PALETTE_GROUPS.map(g => g.id));
    this._isCompact = false;
    this._searchTerm = '';
    this._rootEl = null;
    this._groupBodies = {};
    this._groupHeaders = {};
    this._itemEls = [];

    // Tool state
    this._connectSource = null;
    this._connectHandler = null;
    this._lassoHandlers = null;

    // Theme-aware icons
    const theme = (lf.graphModel && lf.graphModel.__epxTheme) || null;
    this._icons = { ...buildPaletteIcons(theme), ...STATIC_ICONS };
    this._groupThemes = buildGroupThemes(theme);

    injectCSS();
    this._buildDOM();
  }

  updateTheme(theme) {
    this._icons = { ...buildPaletteIcons(theme), ...STATIC_ICONS };
    this._groupThemes = buildGroupThemes(theme);
    // Rebuild palette DOM
    if (this._rootEl && this._containerEl) {
      this._containerEl.removeChild(this._rootEl);
      this._rootEl = null;
      this._groupBodies = {};
      this._groupHeaders = {};
      this._itemEls = [];
      this._buildDOM();
    }
  }

  // ── DOM Building ─────────────────────────────────────────────────────────

  _buildDOM() {
    this._rootEl = document.createElement('div');
    this._rootEl.className = 'epx-palette';

    // Top bar: search + toggle
    this._buildTopBar();

    // Tools section
    this._buildToolsSection();

    // Divider
    const div1 = document.createElement('div');
    div1.className = 'epx-palette-divider';
    this._rootEl.appendChild(div1);

    // Groups wrapper (scrollable)
    this._groupsWrapper = document.createElement('div');
    this._groupsWrapper.className = 'epx-palette-groups';

    for (const group of PALETTE_GROUPS) {
      this._buildGroupSection(group);
    }

    // No results message
    this._noResultsEl = document.createElement('div');
    this._noResultsEl.className = 'epx-palette-no-results';
    this._noResultsEl.style.display = 'none';
    this._noResultsEl.textContent = (this._i18n.palette && this._i18n.palette.noResults) || 'No elements found';
    this._groupsWrapper.appendChild(this._noResultsEl);

    this._rootEl.appendChild(this._groupsWrapper);
    this._containerEl.appendChild(this._rootEl);
  }

  _buildTopBar() {
    const topbar = document.createElement('div');
    topbar.className = 'epx-palette-topbar';

    // Search
    const searchWrap = document.createElement('div');
    searchWrap.className = 'epx-palette-search-wrap';

    const searchIcon = document.createElement('span');
    searchIcon.className = 'epx-palette-search-icon';
    searchIcon.innerHTML = this._icons.search;
    searchWrap.appendChild(searchIcon);

    const searchInput = document.createElement('input');
    searchInput.className = 'epx-palette-search';
    searchInput.type = 'text';
    searchInput.placeholder = (this._i18n.palette && this._i18n.palette.searchPlaceholder) || 'Search...';
    searchInput.addEventListener('input', (e) => {
      this._searchTerm = e.target.value.toLowerCase().trim();
      this._applyFilter();
    });
    searchWrap.appendChild(searchInput);
    topbar.appendChild(searchWrap);

    // Toggle compact/expanded
    const toggleBtn = document.createElement('button');
    toggleBtn.className = 'epx-palette-toggle-btn';
    toggleBtn.innerHTML = this._icons.collapse;
    toggleBtn.title = (this._i18n.palette && this._i18n.palette.toggleView) || 'Toggle view';
    toggleBtn.addEventListener('click', () => {
      this._isCompact = !this._isCompact;
      this._rootEl.classList.toggle('compact', this._isCompact);
      toggleBtn.innerHTML = this._isCompact ? this._icons.expand : this._icons.collapse;
    });
    topbar.appendChild(toggleBtn);

    this._rootEl.appendChild(topbar);
  }

  _buildToolsSection() {
    const section = document.createElement('div');
    section.className = 'epx-palette-tools';

    for (const tool of TOOL_ITEMS) {
      const btn = document.createElement('button');
      btn.className = 'epx-palette-tool-btn';
      btn.innerHTML = this._icons[tool.icon];
      btn.setAttribute('data-tool', tool.id);
      const toolLabel = (this._i18n.palette && this._i18n.palette.tools && this._i18n.palette.tools[tool.i18nKey]) || tool.id;
      btn.setAttribute('title', toolLabel);
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        this._setActiveTool(tool.id);
      });
      section.appendChild(btn);
    }

    // Separator
    const sep = document.createElement('div');
    sep.style.cssText = 'width:1px; height:18px; background:var(--epx-panel-border, #e0e0e0); margin:0 2px;';
    section.appendChild(sep);

    // Action buttons (zoom, fit)
    for (const action of ACTION_ITEMS) {
      const btn = document.createElement('button');
      btn.className = 'epx-palette-tool-btn';
      btn.innerHTML = this._icons[action.icon];
      const label = (this._i18n.palette && this._i18n.palette.tools && this._i18n.palette.tools[action.i18nKey]) || action.id;
      btn.setAttribute('title', label);
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        this._executeAction(action.id);
      });
      section.appendChild(btn);
    }

    this._rootEl.appendChild(section);
  }

  _buildGroupSection(group) {
    const theme = this._groupThemes[group.id] || { bg: '#f5f5f5', border: '#d9d9d9', accent: '#595959', icon: null };
    const groupEl = document.createElement('div');
    groupEl.className = 'epx-palette-group';
    groupEl.setAttribute('data-group', group.id);

    // Header with category color
    const header = document.createElement('div');
    header.className = 'epx-palette-group-header';
    header.style.background = theme.bg;
    header.style.borderColor = theme.border;
    header.style.color = theme.accent;

    // Group icon
    const headerIcon = document.createElement('span');
    headerIcon.className = 'epx-palette-group-header-icon';
    if (theme.icon && this._icons[theme.icon]) {
      headerIcon.innerHTML = this._icons[theme.icon];
    }
    header.appendChild(headerIcon);

    // Label
    const label = document.createElement('span');
    label.className = 'epx-palette-group-header-label';
    label.textContent = (this._i18n.palette && this._i18n.palette.categories && this._i18n.palette.categories[group.i18nKey]) || group.id;
    header.appendChild(label);

    // Count badge
    const count = document.createElement('span');
    count.className = 'epx-palette-group-count';
    count.textContent = group.items.length;
    header.appendChild(count);

    // Chevron
    const chevron = document.createElement('span');
    chevron.className = 'epx-palette-group-chevron collapsed';
    chevron.textContent = '\u25BE';
    header.appendChild(chevron);

    // Body
    const body = document.createElement('div');
    body.className = 'epx-palette-group-body collapsed';
    body.style.background = theme.bg + '80'; // semi-transparent
    body.style.borderColor = theme.border;

    this._groupBodies[group.id] = body;
    this._groupHeaders[group.id] = { header, chevron };

    header.addEventListener('click', () => {
      const isCollapsed = this._collapsedGroups.has(group.id);
      if (isCollapsed) {
        this._collapsedGroups.delete(group.id);
        body.classList.remove('collapsed');
        chevron.classList.remove('collapsed');
        header.classList.add('expanded');
      } else {
        this._collapsedGroups.add(group.id);
        body.classList.add('collapsed');
        chevron.classList.add('collapsed');
        header.classList.remove('expanded');
      }
    });

    for (const item of group.items) {
      const itemEl = this._buildPaletteItem(item);
      body.appendChild(itemEl);
    }

    groupEl.appendChild(header);
    groupEl.appendChild(body);
    this._groupsWrapper.appendChild(groupEl);
  }

  _buildPaletteItem(item) {
    const el = document.createElement('div');
    el.className = 'epx-palette-item';
    el.draggable = true;

    const itemLabel = (this._i18n.palette && this._i18n.palette[item.i18nKey]) || item.type;
    el.setAttribute('title', itemLabel);
    el.setAttribute('data-search', itemLabel.toLowerCase());

    const iconWrap = document.createElement('div');
    iconWrap.className = 'epx-palette-icon';
    iconWrap.innerHTML = this._icons[item.icon] || '';
    el.appendChild(iconWrap);

    // Label text
    const labelEl = document.createElement('span');
    labelEl.className = 'epx-palette-item-label';
    labelEl.textContent = itemLabel;
    el.appendChild(labelEl);

    // Track for search filtering
    this._itemEls.push({ el, label: itemLabel.toLowerCase(), groupId: item._groupId });

    // Primary: LogicFlow native DnD
    el.addEventListener('mousedown', (e) => {
      if (e.button !== 0) return; // left click only
      const dragData = { type: item.type };
      if (item.properties) {
        dragData.properties = item.properties;
      }
      this._lf.dnd.startDrag(dragData);
    });

    // Fallback: HTML5 drag for external consumers
    el.addEventListener('dragstart', (e) => {
      e.dataTransfer.setData('epx-node-type', item.type);
    });

    return el;
  }

  // ── Search / Filter ─────────────────────────────────────────────────────

  _applyFilter() {
    const term = this._searchTerm;
    let anyVisible = false;

    for (const { el, label } of this._itemEls) {
      const match = !term || label.includes(term);
      el.classList.toggle('filtered-out', !match);
      if (match) anyVisible = true;
    }

    // Auto-expand groups that have matching items when searching
    if (term) {
      for (const group of PALETTE_GROUPS) {
        const body = this._groupBodies[group.id];
        const { header, chevron } = this._groupHeaders[group.id];
        const hasVisible = body.querySelector('.epx-palette-item:not(.filtered-out)');
        if (hasVisible) {
          body.classList.remove('collapsed');
          chevron.classList.remove('collapsed');
          header.classList.add('expanded');
          this._collapsedGroups.delete(group.id);
        } else {
          body.classList.add('collapsed');
          chevron.classList.add('collapsed');
          header.classList.remove('expanded');
          this._collapsedGroups.add(group.id);
        }
      }
    }

    this._noResultsEl.style.display = anyVisible ? 'none' : 'block';
  }

  // ── Tool Management ──────────────────────────────────────────────────────

  _setActiveTool(toolName) {
    // Toggle off if clicking the already-active tool
    if (toolName === this._activeTool) {
      this._deactivateTool(this._activeTool);
      this._activeTool = null;
      const buttons = this._rootEl.querySelectorAll('.epx-palette-tool-btn');
      buttons.forEach(btn => btn.classList.remove('active'));
      return;
    }

    // Deactivate previous
    this._deactivateTool(this._activeTool);

    // Update button states
    const buttons = this._rootEl.querySelectorAll('.epx-palette-tool-btn');
    buttons.forEach(btn => {
      btn.classList.toggle('active', btn.getAttribute('data-tool') === toolName);
    });

    this._activeTool = toolName;

    // Activate new
    this._activateTool(toolName);
  }

  _activateTool(name) {
    switch (name) {
      case 'lasso': this._startLassoTool(); break;
      case 'connect': this._startConnectTool(); break;
    }
  }

  _deactivateTool(name) {
    switch (name) {
      case 'lasso': this._stopLassoTool(); break;
      case 'connect': this._stopConnectTool(); break;
    }
  }

  // ── Actions (zoom / fit) ────────────────────────────────────────────────

  _executeAction(actionId) {
    switch (actionId) {
      case 'zoomIn':     this._lf.zoom(true);  break;
      case 'zoomOut':    this._lf.zoom(false); break;
      case 'fitView':    this._onFitView    ? this._onFitView()    : this._lf.fitView(20); break;
      case 'autoLayout': this._onAutoLayout ? this._onAutoLayout() : null; break;
    }
  }

  // ── Lasso Tool ───────────────────────────────────────────────────────────

  _startLassoTool() {
    const container = this._lf.graphModel.rootEl;
    if (!container) return;

    let startX, startY, rectEl;

    const onMouseDown = (e) => {
      // Only start lasso on direct canvas clicks (not on nodes)
      if (e.target.closest('.lf-node') || e.target.closest('.lf-edge')) return;
      startX = e.clientX;
      startY = e.clientY;

      rectEl = document.createElement('div');
      rectEl.className = 'epx-lasso-rect';
      container.appendChild(rectEl);

      const onMouseMove = (e2) => {
        if (!rectEl) return;
        const x = Math.min(startX, e2.clientX);
        const y = Math.min(startY, e2.clientY);
        const w = Math.abs(e2.clientX - startX);
        const h = Math.abs(e2.clientY - startY);

        const containerRect = container.getBoundingClientRect();
        rectEl.style.left = (x - containerRect.left) + 'px';
        rectEl.style.top = (y - containerRect.top) + 'px';
        rectEl.style.width = w + 'px';
        rectEl.style.height = h + 'px';
      };

      const onMouseUp = (e2) => {
        document.removeEventListener('mousemove', onMouseMove);
        document.removeEventListener('mouseup', onMouseUp);

        if (rectEl && rectEl.parentNode) {
          rectEl.parentNode.removeChild(rectEl);
        }
        rectEl = null;

        // Convert screen coordinates to canvas coordinates
        const topLeft = this._lf.getPointByClient({ x: Math.min(startX, e2.clientX), y: Math.min(startY, e2.clientY) });
        const bottomRight = this._lf.getPointByClient({ x: Math.max(startX, e2.clientX), y: Math.max(startY, e2.clientY) });

        const elements = this._lf.getAreaElement(
          [topLeft.x, topLeft.y],
          [bottomRight.x, bottomRight.y]
        );

        if (elements && elements.length) {
          elements.forEach(el => {
            this._lf.selectElementById(el.id, true);
          });
          // Use elements from getAreaElement directly — avoids isSelected timing issues
          if (this._onSelectionChanged) {
            this._onSelectionChanged(elements);
          }
        }
      };

      document.addEventListener('mousemove', onMouseMove);
      document.addEventListener('mouseup', onMouseUp);
    };

    container.addEventListener('mousedown', onMouseDown);

    // Auto-deactivate lasso once SelectionSelect completes a selection
    const onSelectionDone = () => {
      this._lf.off('selection:selected', onSelectionDone);
      this._deactivateLasso();
    };
    this._lf.on('selection:selected', onSelectionDone);

    this._lassoHandlers = { container, onMouseDown, onSelectionDone };

    // Prevent canvas pan during lasso
    this._lf.updateEditConfig({ stopMoveGraph: true });
  }

  _deactivateLasso() {
    this._stopLassoTool();
    this._activeTool = null;
    const buttons = this._rootEl.querySelectorAll('.epx-palette-tool-btn');
    buttons.forEach(btn => btn.classList.remove('active'));
  }

  _stopLassoTool() {
    if (this._lassoHandlers) {
      this._lassoHandlers.container.removeEventListener('mousedown', this._lassoHandlers.onMouseDown);
      if (this._lassoHandlers.onSelectionDone) {
        this._lf.off('selection:selected', this._lassoHandlers.onSelectionDone);
      }
      this._lassoHandlers = null;
    }
    this._lf.updateEditConfig({ stopMoveGraph: false });
  }

  // ── Connect Tool ─────────────────────────────────────────────────────────

  _startConnectTool() {
    this._connectSource = null;

    this._connectHandler = ({ data }) => {
      if (!this._connectSource) {
        this._connectSource = data.id;
        // Visual feedback: highlight source
        const model = this._lf.graphModel.getElement(data.id);
        if (model) {
          model.setProperties({ _connectHighlight: true });
        }
      } else {
        const targetId = data.id;
        if (targetId !== this._connectSource) {
          this._lf.addEdge({
            sourceNodeId: this._connectSource,
            targetNodeId: targetId,
            type: 'epx:SequenceFlow',
          });
        }
        // Clear highlight
        const sourceModel = this._lf.graphModel.getElement(this._connectSource);
        if (sourceModel) {
          sourceModel.setProperties({ _connectHighlight: false });
        }
        this._connectSource = null;
      }
    };

    this._lf.on('node:click', this._connectHandler);
  }

  _stopConnectTool() {
    if (this._connectHandler) {
      this._lf.off('node:click', this._connectHandler);
      this._connectHandler = null;
    }
    if (this._connectSource) {
      const model = this._lf.graphModel.getElement(this._connectSource);
      if (model) {
        model.setProperties({ _connectHighlight: false });
      }
      this._connectSource = null;
    }
  }

  // ── Cleanup ──────────────────────────────────────────────────────────────

  destroy() {
    this._deactivateTool(this._activeTool);
    if (this._rootEl && this._rootEl.parentNode) {
      this._rootEl.parentNode.removeChild(this._rootEl);
    }
  }
}
